import React from 'react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import { CopyableId, Dialog, DialogContent, DialogHeader, DialogTitle } from '@duck/ui'
import { AssignmentActionsDropdown } from '@/components/PartnerCohorts/Assignments/AssignmentActionsDropdown'
import { useAssignmentActions } from '@/components/PartnerCohorts/Assignments/useAssignmentActions'
import { SendAssignment } from '@/components/PartnerCohorts/Assignments/SendAssignment'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'

interface AssignmentsSectionProps {
  assignments: UserService.AdminUserAssignment[]
  onAssignmentChange?: () => void
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-success/10 text-success',
  inactive: 'bg-muted/10 text-muted-foreground',
  sent: 'bg-blue-500/10 text-blue-600',
  draft: 'bg-muted/10 text-muted-foreground',
}

const AssignmentActionsCell: React.FC<{
  assignment: UserService.AdminUserAssignment
  onAssignmentChange?: () => void
}> = ({ assignment, onAssignmentChange }) => {
  const { t } = useTar()
  const {
    handleEdit, handleDelete, handleRestore, handleSetStatus, isActive,
    isEditDialogOpen, setIsEditDialogOpen, isDeleteDialogOpen, setIsDeleteDialogOpen,
  } = useAssignmentActions({
    assignment: { id: assignment.assignmentId, status: assignment.status as PartnerService.AssignmentStatus },
    onAssignmentChange,
  })

  return (
    <>
      <AssignmentActionsDropdown
        isActive={isActive}
        onEdit={handleEdit}
        onDeleteClick={() => setIsDeleteDialogOpen(true)}
        onSetStatus={handleSetStatus}
        linkToken={assignment.linkToken}
        isDeleted={!!assignment.deletedAt}
        onRestoreClick={() => { void handleRestore() }}
      />

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent width="xs">
          <DialogHeader><DialogTitle>{t('common.editAssignment')}</DialogTitle></DialogHeader>
          {/* Admins have full task visibility, so SendAssignment's task picker
              resolves this assignment's task (no access-group scoping applies). */}
          <SendAssignment
            assignmentId={assignment.assignmentId}
            parentManagesCohort
            onClose={() => setIsEditDialogOpen(false)}
            onSend={() => { setIsEditDialogOpen(false); onAssignmentChange?.() }}
          />
        </DialogContent>
      </Dialog>

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title={t('common.deleteAssignment')}
        description={t('common.assignmentDeleteReversibleDescription')}
      />
    </>
  )
}

export const AssignmentsSection: React.FC<AssignmentsSectionProps> = ({ assignments, onAssignmentChange }) => {
  const { t } = useTar()

  if (assignments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {t('partner.noAssignments')}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="text-start py-2 px-3 font-medium">{t('common.id')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('tasks.task')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('common.organization')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('common.status')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('common.startDate')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('partner.dueDate')}</th>
            <th className="text-start py-2 px-3 font-medium">{t('common.createdAt')}</th>
            <th className="py-2 px-3">
              <span className="sr-only">{t('common.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((a) => (
            <tr key={a.assignmentId} className="border-b border-border/50 hover:bg-muted/5">
              <td className="py-2">
                <CopyableId value={a.assignmentId} />
              </td>
              <td className="py-2 px-3 ps-0">{a.taskName || a.taskId}</td>
              <td className="py-2 px-3">{a.organizationName || a.organizationId}</td>
              <td className="py-2 px-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || 'bg-muted/10 text-muted-foreground'}`}>
                  {a.status}
                </span>
                {a.deletedAt && (
                  <span
                    title={new Date(a.deletedAt).toLocaleString()}
                    className="ms-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error"
                  >
                    {t('common.deleted')}
                  </span>
                )}
              </td>
              <td className="py-2 px-3">
                {a.startDate ? <RelativeDateTime isoDateString={a.startDate} /> : '-'}
              </td>
              <td className="py-2 px-3">
                {a.dueDate ? <RelativeDateTime isoDateString={a.dueDate} /> : '-'}
              </td>
              <td className="py-2 px-3">
                {a.createdAt ? <RelativeDateTime isoDateString={a.createdAt} /> : '-'}
              </td>
              <td className="py-2 px-1 text-end">
                <AssignmentActionsCell assignment={a} onAssignmentChange={onAssignmentChange} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
